import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { css } from '@compiled/react';
import { graphql } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { IconButton } from '@atlaskit/button/new';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import CrossIcon from '@atlaskit/icon/core/cross';
import EditIcon from '@atlaskit/icon/core/edit';
import RetryIcon from '@atlaskit/icon/core/retry';
import LikeIcon from '@atlaskit/icon/core/thumbs-up';
import DislikeIcon from '@atlaskit/icon/core/thumbs-down';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { FlagsProvider, useFlags } from '@atlaskit/flag';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { toDocumentNode } from '@confluence/apollo-relay-interop/entry-points/toDocumentNode.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { SpaceOverviewSwitcherQuery } from '../SpaceOverviewSwitcher.tsx';
import { useRegenerateLoadingState } from '../useRegenerateLoadingState.ts';

import type { SmartSpaceOverviewData } from './SmartSpaceOverview.tsx';
import type {
	SmartSpaceOverviewSummaryUpdateMutation$data,
	SmartSpaceOverviewSummaryUpdateMutation$variables,
} from './__generated__/SmartSpaceOverviewSummaryUpdateMutation.graphql.ts';

export type SummaryData = NonNullable<SmartSpaceOverviewData['summary']>;

type VoteDirection = 'up' | 'down' | null;
type SmartSpaceOverviewSummaryViewProps = {
	body: string;
	spaceKey: string;
	isPinned: boolean;
	onSave: (newBody: string) => void;
};

const SmartSpaceOverviewSummaryViewInner = ({
	body,
	spaceKey,
	isPinned,
	onSave,
}: SmartSpaceOverviewSummaryViewProps) => {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlags();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isHovered, setIsHovered] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [editText, setEditText] = useState(body);
	const [vote, setVote] = useState<VoteDirection>(null);
	const [startRefresh, isRefreshing] = useRegenerateLoadingState();

	const handleMouseEnter = useCallback(() => setIsHovered(true), []);
	const handleMouseLeave = useCallback(() => setIsHovered(false), []);

	const showFeedbackFlag = useCallback(() => {
		showFlag({
			title: formatMessage(i18n.feedbackFlagTitle),
			description: formatMessage(i18n.feedbackFlagDescription),
			icon: <StatusSuccessIcon color={token('color.icon.success')} label="success" />,
			isAutoDismiss: true,
		});
	}, [showFlag, formatMessage]);

	const fireFeedbackEvent = useCallback(
		(wasHelpful: boolean) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'submitted',
					actionSubject: 'feedback',
					actionSubjectId: 'summary',
					source: 'smartSpaceOverview',
					attributes: {
						moduleType: 'summary',
						spaceKey,
						wasHelpful,
						isPinned,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, spaceKey, isPinned],
	);

	const fireEditedEvent = useCallback(
		(previousBody: string, newBody: string) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'edited',
					actionSubject: 'text',
					actionSubjectId: 'summary',
					source: 'smartSpaceOverview',
					attributes: {
						moduleType: 'summary',
						spaceKey,
						previousSummaryLength: previousBody.length,
						newSummaryLength: newBody.length,
						isPinned,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, spaceKey, isPinned],
	);

	const handleThumbsUp = useCallback(() => {
		setVote('up');
		showFeedbackFlag();
		fireFeedbackEvent(true);
	}, [showFeedbackFlag, fireFeedbackEvent]);

	const handleThumbsDown = useCallback(() => {
		setVote('down');
		showFeedbackFlag();
		fireFeedbackEvent(false);
	}, [showFeedbackFlag, fireFeedbackEvent]);

	const handleRefresh = useCallback(() => {
		setVote(null);
		startRefresh();
	}, [startRefresh]);

	const onEdit = useCallback(() => {
		setEditText(body);
		setIsEditing(true);
	}, [body]);

	const onConfirm = useCallback(() => {
		if (body !== editText) {
			fireEditedEvent(body, editText);
			onSave(editText);
		}
		setIsEditing(false);
	}, [body, editText, fireEditedEvent, onSave]);

	const onCancel = useCallback(() => {
		setEditText(body);
		setIsEditing(false);
	}, [body]);

	const onKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === 'Escape') {
				onCancel();
			}
		},
		[onCancel],
	);

	if (isEditing) {
		return (
			<div css={styles.editContainer} data-testid="smart-space-overview-summary-edit">
				<TextArea
					value={editText}
					onChange={(e) => setEditText(e.target.value)}
					onKeyDown={onKeyDown}
					minimumRows={3}
					resize="vertical"
					testId="smart-space-overview-summary-edit-input"
				/>
				<div css={styles.editActions}>
					<Tooltip content={formatMessage(i18n.confirm)}>
						<IconButton
							icon={CheckMarkIcon}
							label={formatMessage(i18n.confirm)}
							appearance="default"
							onClick={onConfirm}
							testId="smart-space-overview-summary-confirm-button"
						/>
					</Tooltip>
					<Tooltip content={formatMessage(i18n.cancel)}>
						<IconButton
							icon={CrossIcon}
							label={formatMessage(i18n.cancel)}
							appearance="default"
							onClick={onCancel}
							testId="smart-space-overview-summary-cancel-button"
						/>
					</Tooltip>
				</div>
			</div>
		);
	}

	return (
		<div
			css={styles.summaryContainer}
			data-testid="smart-space-overview-summary"
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onFocus={handleMouseEnter}
			onBlur={handleMouseLeave}
		>
			<div
				css={[
					styles.summaryBody,
					summaryBodyLineHeightStyle,
					isRefreshing && styles.contentBlurred,
				]}
			>
				{body}
			</div>
			<div
				css={[styles.actionsContainer, actionsContainerTransitionStyle]}
				style={{
					opacity: isHovered ? 1 : 0,
					pointerEvents: isHovered ? 'auto' : 'none',
				}}
			>
				<div css={styles.actionsContent}>
					<Tooltip content={formatMessage(i18n.edit)}>
						<IconButton
							icon={() => <EditIcon label="" color={token('color.icon.subtlest')} />}
							label={formatMessage(i18n.edit)}
							appearance="subtle"
							spacing="compact"
							onClick={onEdit}
							testId="smart-space-overview-summary-edit-button"
						/>
					</Tooltip>
					{fg('smart-space-overview-refresh') && (
						<Tooltip content={formatMessage(i18n.refresh)}>
							<IconButton
								icon={() => <RetryIcon label="" color={token('color.icon.subtlest')} />}
								label={formatMessage(i18n.refresh)}
								appearance="subtle"
								spacing="compact"
								isLoading={isRefreshing}
								onClick={handleRefresh}
								testId="smart-space-overview-summary-refresh-button"
							/>
						</Tooltip>
					)}
					<IconButton
						icon={() => (
							<LikeIcon
								label=""
								color={
									vote === 'up' ? token('color.icon.accent.blue') : token('color.icon.subtlest')
								}
							/>
						)}
						label={formatMessage(i18n.thumbsUp)}
						appearance="subtle"
						spacing="compact"
						isSelected={vote === 'up'}
						isTooltipDisabled={false}
						onClick={() => {
							if (vote !== 'up') handleThumbsUp();
						}}
						testId="smart-space-overview-summary-thumbs-up-button"
					/>
					<IconButton
						icon={() => (
							<DislikeIcon
								label=""
								color={
									vote === 'down' ? token('color.icon.accent.blue') : token('color.icon.subtlest')
								}
							/>
						)}
						label={formatMessage(i18n.thumbsDown)}
						appearance="subtle"
						spacing="compact"
						isSelected={vote === 'down'}
						isTooltipDisabled={false}
						onClick={() => {
							if (vote !== 'down') handleThumbsDown();
						}}
						testId="smart-space-overview-summary-thumbs-down-button"
					/>
				</div>
			</div>
		</div>
	);
};

export const SmartSpaceOverviewSummaryView = (props: SmartSpaceOverviewSummaryViewProps) => (
	<FlagsProvider>
		<SmartSpaceOverviewSummaryViewInner {...props} />
	</FlagsProvider>
);

type SmartSpaceOverviewSummaryProps = {
	summaryData: SummaryData | null | undefined;
	spaceKey: string;
};

export const SmartSpaceOverviewSummary = ({
	summaryData,
	spaceKey,
}: SmartSpaceOverviewSummaryProps) => {
	const { cloudId } = useSessionData();

	const queryDocumentNode = toDocumentNode(SpaceOverviewSwitcherQuery);

	const [updateSummary] = useMutationInterop<
		SmartSpaceOverviewSummaryUpdateMutation$data,
		SmartSpaceOverviewSummaryUpdateMutation$variables
	>(UpdateSmartSpaceOverviewSummaryMutation);

	const onSave = useCallback(
		(newBody: string) => {
			void updateSummary({
				variables: {
					cloudId,
					input: {
						spaceKey,
						summary: {
							body: newBody,
						},
					},
				},
				optimisticResponse: {
					confluence_updateSmartSpaceOverview: {
						success: true,
						errors: null,
						overview: {
							summary: {
								body: newBody,
							},
						},
					},
				},
				update: (cache, { data: mutationData }) => {
					const updatedBody =
						mutationData?.confluence_updateSmartSpaceOverview?.overview?.summary?.body;
					if (updatedBody == null) {
						return;
					}
					cache.writeQuery({
						query: queryDocumentNode,
						variables: { cloudId, spaceKey },
						data: {
							confluence_smartSpaceOverview: {
								__typename: 'ConfluenceSmartSpaceOverview',
								summary: {
									__typename: 'ConfluenceSmartSpaceOverviewSummary',
									body: updatedBody,
								},
							},
						},
					});
				},
			});
		},
		[cloudId, spaceKey, updateSummary, queryDocumentNode],
	);

	const body = summaryData?.body;
	if (!body) {
		return null;
	}

	return (
		<SmartSpaceOverviewSummaryView
			body={body}
			spaceKey={spaceKey}
			isPinned={summaryData?.isPinned ?? false}
			onSave={onSave}
		/>
	);
};

const UpdateSmartSpaceOverviewSummaryMutation = graphql`
	mutation SmartSpaceOverviewSummaryUpdateMutation(
		$cloudId: ID!
		$input: ConfluenceUpdateSmartSpaceOverviewInput!
	) {
		confluence_updateSmartSpaceOverview(cloudId: $cloudId, input: $input) {
			success
			errors {
				message
			}
			overview {
				summary {
					body
				}
			}
		}
	}
`;

const i18n = defineMessages({
	edit: {
		id: 'space-overview.smart-space-overview.summary.edit',
		defaultMessage: 'Edit',
		description: 'Tooltip and label for the edit button on the space overview summary',
	},
	confirm: {
		id: 'space-overview.smart-space-overview.summary.confirm',
		defaultMessage: 'Confirm',
		description: 'Tooltip and label for the confirm button when editing the space overview summary',
	},
	cancel: {
		id: 'space-overview.smart-space-overview.summary.cancel',
		defaultMessage: 'Cancel',
		description: 'Tooltip and label for the cancel button when editing the space overview summary',
	},
	refresh: {
		id: 'space-overview.smart-space-overview.summary.refresh',
		defaultMessage: 'Refresh',
		description: 'Tooltip and label for the refresh button on the space overview summary',
	},
	thumbsUp: {
		id: 'space-overview.smart-space-overview.summary.thumbs-up',
		defaultMessage: 'Good summary',
		description: 'Label for the thumbs up button on the space overview summary',
	},
	thumbsDown: {
		id: 'space-overview.smart-space-overview.summary.thumbs-down',
		defaultMessage: 'Bad summary',
		description: 'Label for the thumbs down button on the space overview summary',
	},
	feedbackFlagTitle: {
		id: 'space-overview.smart-space-overview.summary.feedback-flag-title',
		defaultMessage: 'Thanks!',
		description:
			'Title of the success flag shown after a user submits feedback on the space overview summary.',
	},
	feedbackFlagDescription: {
		id: 'space-overview.smart-space-overview.summary.feedback-flag-description',
		defaultMessage: 'We appreciate you taking the time to send this feedback.',
		description:
			'Description of the success flag shown after a user submits feedback on the space overview summary.',
	},
});

// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
const summaryBodyLineHeightStyle = css({ lineHeight: '22px' });

const actionsContainerTransitionStyle = css({ transition: 'opacity 0.2s ease-in-out' });

const styles = cssMap({
	summaryContainer: {
		position: 'relative',
	},
	summaryBody: {
		color: token('color.text'),
		font: token('font.body'),
		whiteSpace: 'pre-wrap',
	},
	actionsContainer: {
		display: 'flex',
		position: 'absolute',
		top: 0,
		insetInlineEnd: 0,
		paddingInlineStart: token('space.300'),
		backgroundImage: `linear-gradient(to left, ${token('elevation.surface')} 70%, transparent 100%)`,
	},
	actionsContent: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'nowrap',
		flexShrink: 0,
	},
	editContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.050'),
		backgroundColor: token('color.background.neutral.subtle'),
		borderRadius: token('radius.medium'),
		padding: token('space.050'),
	},
	editActions: {
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'center',
		gap: token('space.050'),
	},
	contentBlurred: {
		opacity: token('opacity.disabled'),
		transition: 'opacity 150ms ease-in',
		pointerEvents: 'none',
	},
});
